import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Backpack,
  ClipboardList,
  GraduationCap,
  Handshake,
  Mail,
  Presentation,
} from "lucide-react";
import { Nav } from "@/components/site/Nav";
import { Footer } from "@/components/site/Contact";
import { Asterisk, EASE, Reveal } from "@/components/site/Reveal";
import { scrollToId } from "@/lib/api";
import { usePageMeta } from "@/lib/usePageMeta";

const EMAIL = "nbenachour@milestonepediatrictherapyllc.com";

const PACKAGES = [
  {
    icon: ClipboardList,
    title: "Behavior Support",
    items: ["Classroom observations", "Behavior strategies", "Staff consultation", "Recommendations"],
  },
  {
    icon: GraduationCap,
    title: "Student Support",
    items: ["Individualized ABA support", "Skill building", "Behavior reduction", "Classroom participation"],
  },
  {
    icon: Presentation,
    title: "Staff Training",
    items: ["ABA strategies", "Behavior prevention", "Reinforcement", "Classroom management", "Autism support"],
  },
  {
    icon: Backpack,
    title: "School Readiness",
    items: ["Communication", "Following routines", "Transitions", "Social skills", "Independence"],
  },
  {
    icon: Handshake,
    title: "Customized Partnership",
    items: ["Ongoing BCBA consultation", "Staff coaching", "Individual or classroom support", "Customized service hours"],
    featured: true,
  },
];

const SETTINGS = ["Preschools", "Daycares", "Private Schools", "Charter Schools", "Learning Centers"];

const CONTRACTS = [
  { title: "One-Time Consultation", text: "A focused visit to observe, assess, and recommend practical next steps." },
  { title: "Hourly Support", text: "Flexible BCBA and ABA support scheduled around your classroom needs." },
  { title: "Monthly Packages", text: "Ongoing consultation, coaching, and student support on a steady rhythm." },
  { title: "Customized School Contracts", text: "A partnership designed around your students, staff, and community." },
];

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export default function SchoolSupport() {
  usePageMeta(
    "School & Daycare ABA Support | Milestone Pediatric Therapy",
    "Flexible behavioral support for schools, preschools, and childcare centers — BCBA consultation, classroom observations, staff training, and customized partnerships."
  );
  return (
    <div data-testid="school-support-page" className="min-h-screen bg-background">
      <Nav />
      <main>
        <section className="relative overflow-hidden pt-32 pb-20 md:pt-40 md:pb-24">
          <div className="absolute -top-32 -right-40 h-[30rem] w-[30rem] rounded-full bg-brand-pale/80 blur-3xl" aria-hidden="true" />
          <div className="absolute top-1/2 -left-48 h-80 w-80 rounded-full bg-brand-yellow/30 blur-3xl" aria-hidden="true" />
          <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: EASE }}
              className="inline-flex items-center gap-2.5 rounded-full border border-brand-lightblue/60 bg-white/70 backdrop-blur px-5 py-2 mb-8"
              data-testid="school-eyebrow"
            >
              <Asterisk className="h-3.5 w-3.5 text-brand-coral" />
              <span className="text-xs md:text-sm uppercase tracking-[0.22em] font-semibold text-brand-blue">
                For Schools & Childcare Partners
              </span>
            </motion.div>

            <h1 className="font-serif font-medium tracking-tight text-brand-ink text-5xl sm:text-6xl lg:text-7xl leading-[1.04] max-w-4xl" data-testid="school-heading">
              <MaskedLine delay={0.15}>School & Daycare</MaskedLine>
              <MaskedLine delay={0.3}>
                <em className="text-brand-coral">ABA Support</em>
              </MaskedLine>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.55, ease: EASE }}
              className="mt-7 max-w-2xl text-base md:text-lg leading-relaxed text-brand-muted"
              data-testid="school-subcopy"
            >
              Flexible behavioral support for schools, preschools, and childcare centers.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.7, ease: EASE }}
              className="mt-10 flex flex-wrap gap-4"
            >
              <a
                href={`mailto:${EMAIL}?subject=School%20%26%20Daycare%20ABA%20Support%20Inquiry`}
                data-testid="school-cta-partner"
                className="group inline-flex items-center gap-2 rounded-full bg-brand-coral text-white font-semibold px-8 py-4 shadow-soft transition-[transform,box-shadow,background-color] duration-300 hover:-translate-y-1 hover:shadow-lift hover:bg-[#f06666]"
              >
                Partner With Us
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </a>
              <button
                data-testid="school-cta-packages"
                onClick={() => scrollToId("packages")}
                className="inline-flex items-center gap-2 rounded-full border border-brand-blue/30 text-brand-blue font-semibold px-8 py-4 transition-[transform,background-color,border-color] duration-300 hover:-translate-y-1 hover:bg-brand-pale hover:border-brand-blue/50"
              >
                See Support Packages
              </button>
            </motion.div>
          </div>
        </section>

        <section id="packages" className="py-20 md:py-28 bg-white/60 border-y border-brand-lightblue/30">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <Reveal>
              <div className="flex items-center gap-3 mb-5">
                <Asterisk className="h-4 w-4 text-brand-coral" />
                <span className="text-sm uppercase tracking-[0.22em] font-medium text-brand-coral">
                  Support Packages
                </span>
              </div>
              <h2 className="font-serif text-4xl sm:text-5xl font-medium tracking-tight text-brand-blue mb-14" data-testid="packages-heading">
                Support that fits <em className="text-brand-coral">your classrooms.</em>
              </h2>
            </Reveal>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="packages-grid">
              {PACKAGES.map((p, i) => (
                <Reveal key={p.title} delay={0.07 * i}>
                  <article
                    data-testid={`package-${p.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className={`group h-full rounded-[2rem] border p-8 transition-[transform,box-shadow,border-color] duration-500 hover:-translate-y-2 hover:shadow-lift ${
                      p.featured
                        ? "bg-brand-blue text-white border-brand-blue shadow-soft"
                        : "bg-white border-brand-lightblue/40 shadow-soft hover:border-brand-coral/40"
                    }`}
                  >
                    <span
                      className={`flex h-12 w-12 items-center justify-center rounded-2xl mb-6 transition-transform duration-500 group-hover:scale-110 ${
                        p.featured ? "bg-white/15 text-brand-yellow" : "bg-brand-pale text-brand-blue"
                      }`}
                    >
                      <p.icon className="h-6 w-6" />
                    </span>
                    <h3 className={`font-serif text-2xl font-semibold mb-5 ${p.featured ? "text-white" : "text-brand-blue"}`}>
                      {p.title}
                    </h3>
                    <ul className="space-y-2.5">
                      {p.items.map((item) => (
                        <li key={item} className={`flex items-start gap-2.5 text-sm md:text-base ${p.featured ? "text-white/85" : "text-brand-muted"}`}>
                          <span className={`mt-2 h-1.5 w-1.5 rounded-full shrink-0 ${p.featured ? "bg-brand-yellow" : "bg-brand-coral"}`} />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </article>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 md:py-28">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="grid lg:grid-cols-2 gap-12 items-start">
              <div>
                <Reveal>
                  <div className="flex items-center gap-3 mb-5">
                    <Asterisk className="h-4 w-4 text-brand-coral" />
                    <span className="text-sm uppercase tracking-[0.22em] font-medium text-brand-coral">
                      We Can Support
                    </span>
                  </div>
                  <h2 className="font-serif text-4xl sm:text-5xl font-medium tracking-tight text-brand-blue mb-8" data-testid="settings-heading">
                    Wherever children <em className="text-brand-coral">learn and play.</em>
                  </h2>
                </Reveal>
                <div className="flex flex-wrap gap-3" data-testid="settings-chips">
                  {SETTINGS.map((s) => (
                    <span
                      key={s}
                      className="rounded-full border border-brand-lightblue/60 bg-white px-5 py-2.5 text-sm font-medium text-brand-blue shadow-soft transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-brand-pale"
                    >
                      {s}
                    </span>
                  ))}
                </div>
                <Reveal delay={0.15}>
                  <p className="mt-8 text-sm md:text-base leading-relaxed text-brand-muted max-w-xl" data-testid="school-scope-note">
                    Services may include BCBA consultation, classroom observations, behavior support
                    planning, staff training, caregiver collaboration, and individualized ABA services
                    based on the needs and scope of the partnership.
                  </p>
                </Reveal>
              </div>

              <div>
                <Reveal>
                  <h3 className="font-serif text-3xl font-semibold text-brand-blue mb-8" data-testid="contracts-heading">
                    Flexible Contract Options
                  </h3>
                </Reveal>
                <div className="grid sm:grid-cols-2 gap-5" data-testid="contracts-grid">
                  {CONTRACTS.map((c, i) => (
                    <Reveal key={c.title} delay={0.07 * i}>
                      <div
                        data-testid={`contract-${c.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                        className="h-full rounded-3xl bg-white border border-brand-lightblue/40 shadow-soft p-6 transition-[transform,box-shadow,border-color] duration-500 hover:-translate-y-1.5 hover:shadow-lift hover:border-brand-coral/40"
                      >
                        <h4 className="font-serif text-xl font-semibold text-brand-blue mb-2">{c.title}</h4>
                        <p className="text-sm leading-relaxed text-brand-muted">{c.text}</p>
                      </div>
                    </Reveal>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="pb-24 md:pb-32">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <Reveal>
              <div className="rounded-[2.5rem] bg-brand-blue text-white shadow-lift p-10 md:p-16 text-center" data-testid="school-partner-cta">
                <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight mb-5">
                  Partner With <em className="text-brand-yellow">Milestone Pediatric Therapy</em>
                </h2>
                <p className="max-w-2xl mx-auto text-white/85 text-base md:text-lg leading-relaxed mb-9">
                  Contact us to create a behavioral support plan designed around the needs of your
                  students, staff, and school community.
                </p>
                <a
                  href={`mailto:${EMAIL}?subject=School%20%26%20Daycare%20Partnership`}
                  data-testid="school-partner-email-button"
                  className="group inline-flex items-center gap-2 rounded-full bg-white text-brand-blue font-semibold px-8 py-4 shadow-soft transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-lift"
                >
                  <Mail className="h-4 w-4" />
                  {EMAIL}
                </a>
                <p className="mt-8 text-xs text-white/60 max-w-xl mx-auto" data-testid="school-disclaimer">
                  Services are provided within the applicable professional scope, consent,
                  authorization, and regulatory requirements.
                </p>
              </div>
            </Reveal>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
